import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, Check, Star, ArrowUpRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { user, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiryType: 'general',
    message: '',
    marketingEmail: false,
    marketingSMS: false
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-fill user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        name: prev.name || user.name || '',
        email: prev.email || user.email || ''
      }));
    }
  }, [isAuthenticated, user]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Include marketing preferences in the message
      const marketingPrefs = [];
      if (formData.marketingEmail) marketingPrefs.push('Email updates');
      if (formData.marketingSMS) marketingPrefs.push('SMS updates');
      
      const messageWithPrefs = formData.message + 
        (marketingPrefs.length > 0 ? `\n\n[Marketing Opt-In: ${marketingPrefs.join(', ')}]` : '');

      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        inquiryType: formData.inquiryType,
        message: messageWithPrefs
      });
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        inquiryType: 'general',
        message: '',
        marketingEmail: false,
        marketingSMS: false
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      content: '(619) 640-1633',
      link: 'tel:6196401633'
    },
    {
      icon: Phone,
      title: 'Alternate Phone',
      content: '(619) 272-8317',
      link: 'tel:6192728317'
    },
    {
      icon: Mail,
      title: 'Email',
      content: 'RCAwards@Aol.com',
      link: 'mailto:RCAwards@Aol.com'
    },
    {
      icon: MapPin,
      title: 'Address',
      content: '2509 El Cajon Blvd, San Diego, CA 92104',
      link: 'https://maps.google.com/maps?q=2509+El+Cajon+Blvd,+San+Diego,+CA+92104'
    }
  ];

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'quote', label: 'Quote Request' },
    { value: 'corporate', label: 'Corporate Awards' },
    { value: 'sports', label: 'Sports Awards' },
    { value: 'promotional', label: 'Promotional Products' },
    { value: 'rush', label: 'Rush Order' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div data-testid="contact-page" className="min-h-screen pt-28 pb-20 bg-white">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-6 py-16 text-center mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3 font-semibold">Contact Us</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#1a365d] mb-4 font-bold">Let's Create Something Amazing</h1>
          <p className="text-base text-gray-600 max-w-lg mx-auto">
            Questions, custom orders, or ready to start your project — San Diego's award experts are here to help.
          </p>
        </motion.div>
      </section>

      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100">
              <h2 className="text-2xl text-[#1a365d] mb-6 font-bold">Send Us a Message</h2>
              
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-[#1a365d] mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:border-[#1a365d] focus:ring-1 focus:ring-[#1a365d]/20 outline-none bg-gray-50 text-sm"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a365d] mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:border-[#1a365d] focus:ring-1 focus:ring-[#1a365d]/20 outline-none bg-gray-50 text-sm"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a365d] mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    data-testid="contact-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:border-[#1a365d] focus:ring-1 focus:ring-[#1a365d]/20 outline-none bg-gray-50 text-sm"
                    placeholder="(555) 555-5555"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a365d] mb-2">
                    Inquiry Type *
                  </label>
                  <select
                    data-testid="contact-inquiry-type-select"
                    value={formData.inquiryType}
                    onChange={(e) => setFormData({ ...formData, inquiryType: e.target.value })}
                    required
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 focus:border-[#1a365d] focus:ring-1 focus:ring-[#1a365d]/20 outline-none bg-gray-50 text-sm"
                  >
                    {inquiryTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1a365d] mb-2">
                    Message *
                  </label>
                  <textarea
                    data-testid="contact-message-textarea"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#1a365d] focus:ring-1 focus:ring-[#1a365d]/20 outline-none bg-gray-50 resize-none text-sm"
                    placeholder="Tell us about your project - product type, quantity, deadline, etc."
                  />
                </div>

                {/* Marketing Opt-In Checkboxes */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <p className="text-sm font-medium text-[#1a365d] mb-3">Stay Updated (Optional)</p>
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={formData.marketingEmail}
                        onChange={(e) => setFormData({ ...formData, marketingEmail: e.target.checked })}
                        className="mt-0.5 w-4 h-4 text-[#D4AF37] border-gray-300 rounded focus:ring-[#D4AF37] cursor-pointer"
                      />
                      <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                        <strong className="text-[#1a365d]">Email Updates:</strong> Receive exclusive offers, new product announcements, and special promotions
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={formData.marketingSMS}
                        onChange={(e) => setFormData({ ...formData, marketingSMS: e.target.checked })}
                        className="mt-0.5 w-4 h-4 text-[#D4AF37] border-gray-300 rounded focus:ring-[#D4AF37] cursor-pointer"
                      />
                      <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                        <strong className="text-[#1a365d]">SMS Updates:</strong> Get text alerts for order updates, flash sales, and time-sensitive deals
                      </span>
                    </label>
                  </div>
                  <p className="text-xs text-gray-400 mt-3">
                    You can unsubscribe at any time. We respect your privacy.
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div data-testid="success-message" className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                    Thank you! We've received your message and will respond within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div data-testid="error-message" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    Oops! Something went wrong. Please try again or call us directly.
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={isSubmitting}
                  className="w-full bg-[#D4AF37] text-[#1a365d] px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[#c9a432] transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Contact Cards */}
            <div className="space-y-3">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <motion.a
                    key={index}
                    href={info.link}
                    target={info.link.startsWith('http') ? '_blank' : undefined}
                    rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    data-testid={`contact-info-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-[#D4AF37]/30 transition-all duration-300 flex items-start gap-3 group"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#1a365d]/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D4AF37]/10 transition-colors">
                      <Icon size={18} className="text-[#1a365d]" />
                    </div>
                    <div>
                      <h4 className="font-medium text-[#1a365d] text-sm mb-0.5">{info.title}</h4>
                      <p className="text-gray-600 text-sm">{info.content}</p>
                    </div>
                  </motion.a>
                );
              })}
            </div>

            {/* Business Hours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-[#1a365d] rounded-2xl p-6 text-white"
            >
              <div className="flex items-center mb-5">
                <Clock size={22} className="text-[#D4AF37] mr-3" />
                <h3 className="text-lg font-semibold">Business Hours</h3>
              </div>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/70">Monday - Friday</span>
                  <span className="font-medium">9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Saturday</span>
                  <span className="font-medium">10:00 AM - 1:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/70">Sunday</span>
                  <span className="font-medium">Closed</span>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-white/10">
                <p className="text-white/50 text-xs">
                  Rush services available for urgent orders. Call us for same-day or next-day turnaround options.
                </p>
              </div>
            </motion.div>

            {/* Why Choose Us */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-6 border border-gray-100"
            >
              <h3 className="text-base font-semibold text-[#1a365d] mb-3">Why Choose RC Awards?</h3>
              <ul className="space-y-2 text-gray-600 text-sm">
                <li className="flex items-start">
                  <Check className="text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" size={16} />
                  Best prices in San Diego
                </li>
                <li className="flex items-start">
                  <Check className="text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" size={16} />
                  4-day standard turnaround
                </li>
                <li className="flex items-start">
                  <Check className="text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" size={16} />
                  Rush services available
                </li>
                <li className="flex items-start">
                  <Check className="text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" size={16} />
                  Free design assistance
                </li>
                <li className="flex items-start">
                  <Check className="text-[#D4AF37] mr-2 flex-shrink-0 mt-0.5" size={16} />
                  Local delivery available
                </li>
              </ul>
            </motion.div>

            {/* Google Reviews Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-6 border border-gray-100"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={16} className="fill-[#D4AF37] text-[#D4AF37]" />
                      ))}
                    </div>
                    <span className="text-lg font-bold text-[#1a365d]">5.0</span>
                  </div>
                  <p className="text-sm text-gray-500">Based on 200+ Google Reviews</p>
                </div>
                <a
                  href="https://www.google.com/maps/place/RC+Awards/@32.7548,-117.13735,17z/data=!4m8!3m7!1s0x80d954d3a5d9b36d:0x2ae1885968717e70!8m2!3d32.7548!4d-117.13735!9m1!1b1!16s%2Fg%2F1tffdh0j"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#1a365d] font-medium hover:text-[#D4AF37] transition-colors flex items-center gap-1"
                >
                  View All <ArrowUpRight size={14} />
                </a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
